import { Button, styled } from '@mui/material';
import { CellValueChangedEvent } from 'ag-grid-enterprise';
import { useMemo, useState } from 'react';

import PageContentHeader from '../../../app/layouts/page-content/page-content-header/page-content-header.component';
import { PageContentContainer } from '../../../app/layouts/page-content/page-content-header/page-content-header.styles';
import { useAppDispatch, useAppSelector } from '../../../app/store/utils/redux.hooks';
import { currentTargetGroupSelector } from '../../../entities/target-group/store/target-group.selectors';
import {
  runClassification,
  updateCompanySource,
} from '../../../entities/target-group/store/target-group.slice';
import ButtonV2 from '../../../shared/components/button/button-v2/button-v2.component';
import TableV2 from '../../../shared/components/table-v2/table-v2.component';
import FeedbackUiDecorator from '../../../shared/decorators/feedback-ui/feedback-ui.decorator';
import { IInfoConfig } from '../../../shared/decorators/feedback-ui/feedback-ui.types';
import { useModal } from '../../../shared/hooks/use-modal.hook';
import AddListDialog from './add-list-dialog/add-list-dialog.component';
import { columnDefs } from './market-mapping-content.constants';

const LaunchButton = styled(Button)(() => ({
  borderRadius: '0.25rem',
  height: '2.25rem',
  minWidth: '5.5rem',
  fontWeight: 600,
  fontSize: '0.75rem',
  letterSpacing: '0.0625rem',
  padding: '0.5rem 1rem',
  textTransform: 'uppercase',
  backgroundColor: '#DCFCE7',
  color: '#15803D',
  marginRight: '8px',
  border: 'none',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#CFFADA',
    boxShadow: 'none',
  },
}));

function MarketMappingContent() {
  const targetGroup = useAppSelector(currentTargetGroupSelector);
  const { isOpened, handleClose, handleOpen } = useModal();
  const dispatch = useAppDispatch();
  const [isLaunchPromptOpen, setIsLaunchPromptOpen] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const onCellValueChanged = (event: CellValueChangedEvent) => {
    dispatch(updateCompanySource({ sourceId: event.data.id, notes: event.newValue }));
  };

  const mutableRowData = useMemo(
    () => targetGroup?.companiesSources?.map(source => ({ ...source })) || [],
    [targetGroup?.companiesSources],
  );

  const updatedColumnDefs = useMemo(() => {
    return columnDefs.map(col => ({
      ...col,
      menuTabs: [],
      suppressHeaderMenuButton: true,
      sortable: false,
    }));
  }, []);

  const handleLaunchButtonClick = () => {
    setIsLaunchPromptOpen(true);
  };

  const handleLaunchAction = async () => {
    try {
      setIsActionLoading(true);
      await dispatch(
        runClassification({
          targetGroupId: targetGroup.id,
          sourceIds: targetGroup.companiesSources.map(source => source.id.toString()),
        }),
      );
      await new Promise(resolve => setTimeout(resolve, 1000));
      setIsLaunchPromptOpen(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleLaunchCancel = () => {
    setIsLaunchPromptOpen(false);
  };

  const handleLaunchClose = () => {
    setIsLaunchPromptOpen(false);
  };

  const launchPromptConfig: IInfoConfig = {
    isInfo: isLaunchPromptOpen,
    handleAction: handleLaunchAction,
    handleClose: handleLaunchClose,
    handleCancel: handleLaunchCancel,
    isActionLoading,
  };

  return (
    <PageContentContainer>
      <PageContentHeader
        title="Market Mapping"
        description={targetGroup.name}
        rightComponent={
          <>
            <FeedbackUiDecorator infoConfig={launchPromptConfig} isChildrenVisible>
              <LaunchButton onClick={handleLaunchButtonClick} disableRipple>
                LAUNCH
              </LaunchButton>
            </FeedbackUiDecorator>
            <ButtonV2 title="Add list" customVariant="secondary-small" handleClick={handleOpen} />
          </>
        }
      />

      <TableV2
        columnDefs={updatedColumnDefs}
        rowData={mutableRowData}
        gridOptions={{
          onCellValueChanged,
          suppressMenuHide: true,
          getMainMenuItems: () => [],
        }}
      />

      <AddListDialog open={isOpened} onClose={handleClose} />
    </PageContentContainer>
  );
}

export default MarketMappingContent;
